import type { FC, ReactNode } from 'react'
import type { ListingSummary } from '@/types'

import Link from 'next/link'
import { useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatBaths, formatNumber, formatPrice } from '@/lib/format'
import { Photo } from '@/components/generals/photo'
import { SaveButton } from '@/components/homes/save-button'

interface ListingCardProps {
    listing: ListingSummary
    sizes?: string
    priority?: boolean
    badge?: ReactNode
    className?: string
    onHover?: (id: string | null) => void
    highlighted?: boolean
}

export const listingPhotos = (listing: ListingSummary) => [
    ...(listing.cover ? [{ id: 'cover', image: listing.cover, image_alt: listing.cover_alt, room: 'Exterior' }] : []),
    ...listing.photos
]

/* The status badge only shows when a home is not simply for sale. */
export const isForSale = (status: string): boolean => /^\s*for sale\s*$/i.test(status)

export const ListingCard: FC<ListingCardProps> = ({
    listing,
    sizes = '(min-width: 1280px) 22vw, (min-width: 768px) 45vw, 92vw',
    priority = false,
    badge,
    className,
    onHover,
    highlighted = false
}): ReactNode => {
    const photos = listingPhotos(listing)
    const scrollerRef = useRef<HTMLDivElement>(null)
    const [index, setIndex] = useState(0)
    const href = `/homes/${listing.slug}`

    const scrollTo = (next: number) => {
        const scroller = scrollerRef.current

        if (!scroller) return

        const clamped = Math.max(0, Math.min(photos.length - 1, next))
        scroller.scrollTo({ left: clamped * scroller.clientWidth, behavior: 'smooth' })
    }

    const handleScroll = () => {
        const scroller = scrollerRef.current

        if (!scroller || !scroller.clientWidth) return

        setIndex(Math.round(scroller.scrollLeft / scroller.clientWidth))
    }

    return (
        <article
            className={cn('group/card relative', className)}
            onMouseEnter={() => onHover?.(listing.id)}
            onMouseLeave={() => onHover?.(null)}
        >
            <div className={cn(
                'relative aspect-[20/19] overflow-hidden rounded-xl bg-muted transition-shadow',
                highlighted && 'ring-2 ring-foreground ring-offset-2'
            )}>
                <div
                    ref={scrollerRef}
                    onScroll={handleScroll}
                    className='no-scrollbar flex h-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain'
                >
                    {photos.length === 0 && (
                        <Link href={href} className='relative block h-full w-full shrink-0' tabIndex={-1} aria-hidden='true'>
                            <Photo src={null} alt={listing.title} sizes={sizes} />
                        </Link>
                    )}

                    {photos.map((photo, photoIndex) => (
                        <Link
                            key={photo.id}
                            href={href}
                            tabIndex={-1}
                            aria-hidden='true'
                            className='relative block h-full w-full shrink-0 snap-center'
                        >
                            <Photo
                                src={photo.image}
                                alt={photo.image_alt}
                                sizes={sizes}
                                priority={priority && photoIndex === 0}
                                className='transition-transform duration-500 motion-safe:group-hover/card:scale-[1.03]'
                            />
                        </Link>
                    ))}
                </div>

                <div className='pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-3'>
                    <div className='flex flex-wrap gap-1.5'>
                        {badge}
                        {!isForSale(listing.status) && (
                            <span className='pointer-events-auto rounded-full bg-foreground px-3 py-1 text-xs font-semibold text-white shadow-sm'>
                                <span data-mdlfy-cms={`listings:${listing.id}:status`}>{listing.status}</span>
                            </span>
                        )}
                        {isForSale(listing.status) && listing.is_new && (
                            <span className='rounded-full bg-white px-3 py-1 text-xs font-semibold shadow-sm'>New listing</span>
                        )}
                    </div>

                    <SaveButton listingId={listing.id} title={listing.title} className='pointer-events-auto -mt-1 -mr-1' />
                </div>

                {photos.length > 1 && (
                    <>
                        <button
                            type='button'
                            onClick={() => scrollTo(index - 1)}
                            aria-label={`Previous photo of ${listing.title}`}
                            className={cn(
                                'absolute top-1/2 left-3 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition hover:scale-105 hover:bg-white focus-visible:flex md:flex md:opacity-0 md:group-hover/card:opacity-100 md:focus-visible:opacity-100',
                                index === 0 && 'md:invisible'
                            )}
                        >
                            <ChevronLeft className='size-4' aria-hidden='true' />
                        </button>

                        <button
                            type='button'
                            onClick={() => scrollTo(index + 1)}
                            aria-label={`Next photo of ${listing.title}`}
                            className={cn(
                                'absolute top-1/2 right-3 hidden size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition hover:scale-105 hover:bg-white focus-visible:flex md:flex md:opacity-0 md:group-hover/card:opacity-100 md:focus-visible:opacity-100',
                                index >= photos.length - 1 && 'md:invisible'
                            )}
                        >
                            <ChevronRight className='size-4' aria-hidden='true' />
                        </button>

                        <div className='pointer-events-none absolute inset-x-0 bottom-3 flex justify-center gap-1.5' aria-hidden='true'>
                            {photos.map((photo, dotIndex) => (
                                <span
                                    key={photo.id}
                                    className={cn('size-1.5 rounded-full bg-white transition-opacity', dotIndex === index ? 'opacity-100' : 'opacity-60')}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <Link href={href} className='mt-3 block rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary'>
                <div className='flex items-start justify-between gap-3'>
                    <h3 className='min-w-0 truncate text-[0.95rem] font-semibold'>
                        {listing.neighborhood && <span data-mdlfy-cms={`listings:${listing.id}:neighborhood`}>{listing.neighborhood}</span>}
                        {listing.neighborhood && listing.city && ', '}
                        {listing.city && <span data-mdlfy-cms={`listings:${listing.id}:city`}>{listing.city}</span>}
                    </h3>

                    {listing.style && (
                        <span className='shrink-0 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground'>
                            <span data-mdlfy-cms={`styles:${listing.style.id}:name`}>{listing.style.name}</span>
                        </span>
                    )}
                </div>

                <p className='mt-0.5 truncate text-[0.95rem] text-muted-foreground'>
                    <span data-mdlfy-cms={`listings:${listing.id}:title`}>{listing.title}</span>
                </p>

                <p className='mt-0.5 text-[0.95rem] text-muted-foreground'>
                    <span>{listing.bedrooms}</span> bd · <span>{formatBaths(listing.bathrooms)}</span> ba
                    {listing.area_sqft ? <> · <span>{formatNumber(listing.area_sqft)}</span> sq ft</> : null}
                </p>

                <p className='mt-1.5 text-[0.95rem] font-semibold'>{formatPrice(listing.price)}</p>
            </Link>
        </article>
    )
}
